import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { db } from '../lib/firebase';
import { doc, getDoc } from 'firebase/firestore';

const ProtectedRoute = ({ children, requireAdmin = false, requireActive = true }) => {
  const { currentUser, loading } = useAuth();
  const [authorized, setAuthorized] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkAuthorization = async () => {
      if (loading) return;
      
      if (!currentUser) {
        setAuthorized(false);
        setChecking(false);
        return;
      }

      // Check email verification
      if (!currentUser.emailVerified) {
        setAuthorized(false);
        setChecking(false);
        return;
      }

      try {
        // Fetch user data from Firestore
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const userData = userSnap.data();
          
          // Check if user is active
          const isActive = userData.isActive !== false;
          
          if (requireActive && !isActive) {
            setAuthorized(false);
            setChecking(false);
            return;
          }
          
          // Check if admin access is required
          if (requireAdmin) {
            const userRoles = userData.role || ['user'];
            const isAdmin = userRoles.includes('admin');
            
            if (!isAdmin) {
              setAuthorized(false);
              setChecking(false);
              return;
            }
          }
          
          setAuthorized(true);
        } else {
          // User document doesn't exist, allow access but with default user role
          if (requireAdmin) {
            setAuthorized(false);
          } else {
            setAuthorized(true);
          }
        }
      } catch (error) {
        console.error('Error checking authorization:', error);
        setAuthorized(false);
      } finally {
        setChecking(false);
      }
    };

    checkAuthorization();
  }, [currentUser, loading, requireAdmin, requireActive]);

  // Show loading spinner
  if (loading || checking) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authorized - redirect to appropriate page
  if (!authorized) {
    if (!currentUser) {
      return <Navigate to="/auth/sign-in" replace />;
    }
    if (!currentUser.emailVerified) {
      return <Navigate to="/auth/verify-email" replace />;
    }
    if (requireActive) {
      return <Navigate to="/account-suspended" replace />;
    }
    if (requireAdmin) {
      return <Navigate to="/unauthorized" replace />;
    }
    return <Navigate to="/auth/sign-in" replace />;
  }

  return children;
};

export default ProtectedRoute;